import { useEffect, useRef, useState, useMemo } from 'react';
import debounce from 'lodash.debounce';

import { LogService } from '../../../../../../../../services/log-service';
import { getPackingSlipPreview } from '../../../../../api';

const DEBOUNCE_DELAY_MS = 1000;

type TemplateInput = {
  header: string;
  itemsHeader: string;
  items: string;
  footer: string;
  size: string;
};

type PackingSlipPreviewResult = {
  previewUrl: string;
  isLoading: boolean;
  fetchError: boolean;
};

export const useGetPackingSlipPreview = (
  header: string,
  itemsHeader: string,
  items: string,
  footer: string,
  size: string,
): PackingSlipPreviewResult => {
  const [previewUrl, setPreviewUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const previousBlobUrl = useRef('');
  const generationRef = useRef(0);

  const debouncedFetch = useMemo(
    () =>
      debounce((template: TemplateInput, generation: number) => {
        getPackingSlipPreview(template)
          .then((data: BlobPart) => {
            if (generationRef.current !== generation) return;
            try {
              const blob = new Blob([data], { type: 'application/pdf' });
              const blobUrl = URL.createObjectURL(blob);

              if (previousBlobUrl.current) {
                URL.revokeObjectURL(previousBlobUrl.current);
              }
              previousBlobUrl.current = blobUrl;
              setPreviewUrl(blobUrl);
            } catch (error) {
              setPreviewUrl('');
              setFetchError(true);
              LogService.logError(new Error('Converting packing slip preview to blob', { cause: error }), {
                template,
              });
            }
          })
          .catch((error: unknown) => {
            if (generationRef.current !== generation) return;
            setPreviewUrl('');
            setFetchError(true);
            LogService.logError(new Error('Fetching packing slip preview', { cause: error }), {
              template,
            });
          })
          .finally(() => {
            if (generationRef.current === generation) {
              setIsLoading(false);
            }
          });
      }, DEBOUNCE_DELAY_MS),
    [],
  );

  useEffect(() => {
    const generation = ++generationRef.current;

    setIsLoading(true);
    setPreviewUrl('');
    setFetchError(false);

    debouncedFetch({ header, itemsHeader, items, footer, size }, generation);

    return () => {
      debouncedFetch.cancel();
    };
  }, [header, itemsHeader, items, footer, size, debouncedFetch]);

  // Cleanup blob URL and cancel pending fetches on unmount
  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
      if (previousBlobUrl.current) {
        URL.revokeObjectURL(previousBlobUrl.current);
      }
    };
  }, [debouncedFetch]);

  return { previewUrl, isLoading, fetchError };
};
